'use client';

import { cn } from '@app/ui/_lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import { Container } from '../Container';
import { SanityImage } from '../types';
import { Media } from '../Media';
import { useRectTracker } from '../_lib/useRectTracker';
import { TextSectionBlock, TextSectionBlockProps } from '../TextSectionBlock';
import { useMotionValueEvent, useScroll } from 'motion/react';
import { useGetPageCurrentScrollContainerRef } from '../LenisScroll';

export type TextStickyImagesItemProps = {
	className?: string;
	image?: SanityImage;
	textSectionBlock?: Omit<TextSectionBlockProps, 'size'>;
	setTallestImageTextItem?: Function;
	tallestImageTextItem?: number;
};

export type TextStickyImagesProps = {
	className?: string;
	reversed?: boolean;
	items: TextStickyImagesItemProps[];
};

export function TextStickyImages(props: TextStickyImagesProps) {
	const [tallestImageTextItem, setTallestImageTextItem] = useState(0);
	const [activeImageIndex, setActiveImageIndex] = useState(0);

	const ticking = useRef<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);

	const { scrollWrapperRef, scrollContentRef } = useGetPageCurrentScrollContainerRef();
	const { scrollY } = useScroll({ container: scrollWrapperRef, target: scrollContentRef, layoutEffect: false });

	useMotionValueEvent(scrollY, 'change', () => {
		if (ticking.current) {
			return;
		}

		window.requestAnimationFrame(() => {
			ticking.current = false;

			const rect = ref.current!.getBoundingClientRect();
			const windowHeight = window.innerHeight;
			const viewportCenter = windowHeight / 2;

			const gap = (rect.height - tallestImageTextItem * props.items.length) / (props.items.length - 1);
			const itemHeight = tallestImageTextItem + gap;

			const rectTopToScreenCenterDistance = viewportCenter - rect.top - tallestImageTextItem / 2;
			const nextIndex = Math.max(0, Math.floor((rectTopToScreenCenterDistance + itemHeight / 2) / itemHeight));
			const index = rectTopToScreenCenterDistance < 0 ? 0 : nextIndex;

			setActiveImageIndex(index);
		});
	});

	return (
		<div className={props.className} data-component='TextStickyImages'>
			<Container>
				{/* Mobile */}
				<div className='grid gap-12 md:hidden'>
					{props.items &&
						props.items.length > 0 &&
						props.items.map((card, index) => (
							<article className='grid gap-8' key={`stacking-image-text-item-${index}`}>
								<StackingImage {...card} className={props.reversed ? 'order-1' : undefined} />
								<StackingText {...card} />
							</article>
						))}
				</div>

				{/* Desktop */}
				<div
					className='max-md:hidden grid gap-8 grid-cols-2 lg:grid-cols-12'
					style={{
						opacity: tallestImageTextItem === 0 ? 0 : 100,
						padding: `${tallestImageTextItem / 2}px 0`,
					}}
				>
					<div
						className={cn(
							'sticky top-1/2 self-start grid gap-16 lg:col-span-7 lg:gap-24 xl:gap-32',
							props.reversed ? 'order-1' : undefined,
						)}
						style={{ transform: `translateY(-${tallestImageTextItem / 2}px)` }}
					>
						{props.items &&
							props.items.length > 0 &&
							props.items.map((card, index) => (
								<div
									key={`stacking-image-item-${index}`}
									className={cn(
										'flex items-center w-full absolute top-0 left-0 transition-opacity duration-normal',
										activeImageIndex === index ? 'opacity-100' : 'opacity-0',
									)}
									style={{ minHeight: `${tallestImageTextItem}px` }}
								>
									<StackingImage {...card} tallestImageTextItem={tallestImageTextItem} setTallestImageTextItem={setTallestImageTextItem} />
								</div>
							))}
					</div>

					<div
						ref={ref}
						className='grid gap-16 lg:col-span-5 lg:gap-24 xl:gap-32'
						style={{
							transform: `translateY(-${tallestImageTextItem / 2}px)`,
							marginBottom: `-${tallestImageTextItem}px`,
						}}
					>
						{props.items &&
							props.items.length > 0 &&
							props.items.map((card, index) => (
								<div key={`stacking-text-item-${index}`} className='flex items-center' style={{ minHeight: `${tallestImageTextItem}px` }}>
									<StackingText {...card} tallestImageTextItem={tallestImageTextItem} setTallestImageTextItem={setTallestImageTextItem} />
								</div>
							))}
					</div>
				</div>
			</Container>
		</div>
	);
}

function StackingImage(props: TextStickyImagesItemProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const containerRect = useRectTracker(containerRef);

	useEffect(() => {
		if (props.tallestImageTextItem !== undefined && props.setTallestImageTextItem && containerRect.height > props.tallestImageTextItem) {
			props.setTallestImageTextItem(containerRect.height);
		}
	}, [containerRect]);

	return (
		<div className='aspect-5/4 rounded-2xl overflow-hidden w-full' ref={containerRef}>
			{props.image && <Media className='h-full [&_img]:h-full' image={props.image} />}
		</div>
	);
}

function StackingText(props: TextStickyImagesItemProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const containerRect = useRectTracker(containerRef);

	useEffect(() => {
		if (props.tallestImageTextItem !== undefined && props.setTallestImageTextItem && containerRect.height > props.tallestImageTextItem) {
			props.setTallestImageTextItem(containerRect.height);
		}
	}, [containerRect.height]);

	return (
		<div className='w-full md:flex md:items-center lg:pl-10 lg:py-14' ref={containerRef}>
			{props.textSectionBlock && <TextSectionBlock size='sm' {...props.textSectionBlock} />}
		</div>
	);
}
